import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralSource, setReferralSource] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const turnstileRef = useRef(null);

  const REFERRAL_OPTIONS = [
    "Friend / Word of mouth",
    "Reddit",
    "YouTube",
    "Facebook",
    "Instagram",
    "TikTok",
    "Google search",
    "Discord",
    "Other",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Please complete the human verification below before submitting.");
      return;
    }
    setLoading(true);
    const result = await register(email, password, name, referralSource, turnstileToken);
    setLoading(false);
    if (result.success) {
      setRegistered(true);
    } else {
      setError(result.error);
      // Captcha tokens are single-use — reset on any backend failure
      setTurnstileToken("");
      try { turnstileRef.current?.reset(); } catch {}
    }
  };

  if (registered) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "#050505" }}>
        <div className="w-full max-w-sm text-center">
          <div className="border border-[#1A1A1A] bg-[#0D0D0D] p-12">
            <Mail className="h-12 w-12 text-[#39FF14] mx-auto mb-6" strokeWidth={1.5} />
            <h1 data-testid="verify-email-prompt" className="text-2xl font-bold uppercase tracking-tight text-[#F2F2F2] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
              Check Your Email
            </h1>
            <p className="text-[#A1A1A1] text-sm mb-6">
              We sent a verification link to <span className="text-[#39FF14]">{email}</span>. Click it to verify your account before checkout.
            </p>
            <Link to="/products">
              <Button className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6">
                Start Browsing
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "#050505" }}>
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-3">Account</p>
          <h1
            className="text-3xl sm:text-4xl font-black uppercase tracking-tighter text-[#F2F2F2]"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Create Account
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
          <div className="space-y-2">
            <Label htmlFor="name" className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Name</Label>
            <Input
              id="name"
              data-testid="register-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:ring-1 focus:ring-[#39FF14] placeholder:text-[#A1A1A1]/50"
              placeholder="Your name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Email</Label>
            <Input
              id="email"
              data-testid="register-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:ring-1 focus:ring-[#39FF14] placeholder:text-[#A1A1A1]/50"
              placeholder="your@email.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Password</Label>
            <Input
              id="password"
              data-testid="register-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:ring-1 focus:ring-[#39FF14] placeholder:text-[#A1A1A1]/50"
              placeholder="Min 6 characters"
            />
          </div>

          {error && (
            <div className="p-3 border border-red-500/30 bg-red-500/10 text-red-400 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="referral" className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#A1A1A1]">Where did you hear about us? <span className="text-[#A1A1A1]/60 normal-case">(optional)</span></Label>
            <select
              id="referral"
              data-testid="register-referral"
              value={referralSource}
              onChange={(e) => setReferralSource(e.target.value)}
              className="w-full bg-[#0D0D0D] border border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14] px-3 py-2 text-sm font-mono"
            >
              <option value="">— Select (optional) —</option>
              {REFERRAL_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {TURNSTILE_SITE_KEY && (
            <div className="flex justify-center" data-testid="register-turnstile">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken("")}
                onError={() => setTurnstileToken("")}
                options={{ theme: "dark" }}
              />
            </div>
          )}

          <Button
            type="submit"
            data-testid="register-submit"
            disabled={loading || (TURNSTILE_SITE_KEY && !turnstileToken)}
            className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6 disabled:opacity-50"
          >
            {loading ? "Creating account..." : "Create Account"}
          </Button>
        </form>

        <p className="mt-6 text-[#A1A1A1] text-sm">
          Already have an account?{" "}
          <Link to="/login" data-testid="go-to-login" className="text-[#39FF14] hover:underline font-mono text-xs uppercase tracking-wider">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
